import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import OptionSelectionContent from '../../OptionSelection/OptionSelectionContent';
import FavoritePoseSelection from '../../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import useRetouchingExtra from '../../../../hooks/useRetouchingExtra';
import { useCustomer } from '../../../../contexts/CustomerContext';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import { OrderPackage } from '../../../../models';
import usePoses from '../../../../hooks/usePoses';
import usePricelist from '../../../../hooks/usePricelist';
import useAddons from '../../../../hooks/useAddons';
import routes from '../../../../routes';
import useQueryString from '../../../../hooks/use-query-string';
import QueryKeys from '../../../../utils/QueryKeys';
import SeniorMultiPose from '../../../PackageBuilders/MultiPose/SeniorMultiPose';
import usePreorderPose from "../../../../hooks/usePreorderPose";
import useHolidayPricelist from "../../../../hooks/useHolidayPricelist";

export default function Builder() {
  // Routing and queries
  const { packageId } = useParams();
  const history = useHistory();
  const { step, opt: optionId } = useQueryString();
  const { favorites, preorderPoseId } = useQueryString();
  const favoriteIds = [favorites].flat().filter((id) => !!id).map((id) => id.toString());

  // Data
  const { customer, setCustomer } = useCustomer();
  const [orderPackage] = useState(new OrderPackage());
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: poses } = usePoses(customer.currentStudent.id, packageId);
  const { data: preorderPose } = usePreorderPose(preorderPoseId)
  const { data: pricelist } = usePricelist(packageId);
  const { data: giftPricelist } = useHolidayPricelist(customer.currentSchool?.id);

  useRetouchingExtra();
  const { data: addons } = useAddons();

  const favoritePoses = favoriteIds.map((id) => poses.find((pose) => pose.id === id));

  // UI
  const isFetchingPoses = useIsFetching({ queryKey: [QueryKeys.STUDENT_IMAGES] });
  const isFetchingBackgrounds = useIsFetching({ queryKey: [QueryKeys.BACKGROUNDS] });
  const isFetchingPricelist = useIsFetching({ queryKey: [QueryKeys.PRICELIST] });

  const isFetching = isFetchingPoses || isFetchingBackgrounds || isFetchingPricelist;

  const onSave = () => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage });

    if (giftPricelist) history.push(routes.holiday.builder({ optional: true, o: orderPackage.id, pid: orderPackage?.packageId }))
    else if (addons.length === 0) {
      console.log('no addons');
      history.push(routes.retouching(orderPackage.id));
    }
    else {
      console.log('addons');
      history.push(routes.seniors.addons(orderPackage.id));
    }
  };

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!(packageId && customer.currentSchool && customer.currentStudent)) return null;
  if (isFetching) return null;
  if (backgrounds.length === 0) return null;

  const background = backgrounds[0];

  return (
    <div>
      {(!step || step === 'pose-selection') && (
        <FavoritePoseSelection
          backgrounds={backgrounds}
          background={background}
          images={poses}
          seniors
        />
      )}
      {step === 'select-package' && favoritePoses.length > 0 && pricelist && (
        <OptionSelectionContent
          isElementary={false}
          foreground={favoritePoses.length > 0 ? favoritePoses[0].watermarkUrl : pricelist.placeholderImage()}
          background={background}
          multiPoseHandler={(selectedOpt) => history.push(routes.seniors.packageBuilder(packageId, { favorites: favoriteIds, opt: selectedOpt.id, step: 'build' }))}
          packageId={packageId}
          accessCodeOnly={false}
        />
      )}
      {step === 'build' && (
        <SeniorMultiPose
          cartId={customer.cart.id}
          packageId={packageId}
          schoolId={customer.currentSchool.id}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          onSave={onSave}
          favoriteIds={favoriteIds}
          orderPackage={orderPackage}
        />
      )}
    </div>
  );
}
